/**
 * Burnout product page — live price on the buy button, and the sale badge.
 *
 * Nothing here rewrites Horizon's markup. The section renders its own cart
 * form; this only appends two spans to the add-to-cart button and one to the
 * price, then keeps their text in step with the selected variant and the
 * quantity.
 *
 * Why JS at all: the variant picker re-renders the section server-side on every
 * option change, so a Liquid-rendered price would already be correct for the
 * variant — but the quantity stepper never touches the server. Multiplying by
 * quantity is the part that has to happen on the client, and once that is here
 * the variant case may as well come along, so both numbers are produced by one
 * piece of arithmetic rather than two that can disagree.
 *
 * Every write is idempotent. The section morphs on variant change and the
 * slideshow mutates on every slide, so the observer below fires constantly;
 * writing only on an actual difference is what stops it feeding itself.
 */

const SECTION = '.bo-pdp';

/**
 * Render cents using the shop's money format string.
 * Handles the four separator/decimal combinations Shopify emits; an unknown
 * placeholder falls through and returns the format untouched rather than
 * printing "NaN" into the button.
 */
function formatMoney(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  if (!match) return format;

  const key = match[1];
  const apostrophe = key.includes('apostrophe_separator');
  const comma = key.includes('comma_separator');
  const group = apostrophe ? "'" : comma ? '.' : ',';
  const decimal = comma ? ',' : '.';
  const places = key.includes('no_decimals') ? 0 : 2;

  const [whole, fraction] = (cents / 100).toFixed(places).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);

  return format.replace(placeholder, fraction ? `${grouped}${decimal}${fraction}` : grouped);
}

/** Set text only when it actually changes, so writes cannot retrigger the observer. */
function setText(el, value) {
  if (el && el.textContent !== value) el.textContent = value;
}

class ProductPageSync {
  #root;
  #data;
  #frame = null;

  constructor(root) {
    this.#root = root;

    const script = root.querySelector('script[data-bo-pdp-data]');
    if (!script) return;

    try {
      this.#data = JSON.parse(script.textContent);
    } catch {
      // A malformed payload must not take the buy button down with it.
      return;
    }
    if (!this.#data?.variants?.length) return;

    this.#observe();
    this.sync();
  }

  get #form() {
    return this.#root.querySelector('form[data-type="add-to-cart-form"]');
  }

  get #quantity() {
    const input = this.#root.querySelector('.product-details input[name="quantity"]');
    const value = parseInt(input?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get #variant() {
    const id = this.#form?.querySelector('input[name="id"]')?.value;
    // Fall back to the first variant so a gift card or a single-variant product,
    // where the hidden input can be absent, still shows a price.
    return this.#data.variants.find((v) => String(v.id) === String(id)) ?? this.#data.variants[0];
  }

  #observe() {
    // Deliberately a timer rather than requestAnimationFrame: rAF does not run
    // while the document is hidden, so a background tab — or a restored bfcache
    // page — would show a stale total the moment it came back. A 0ms timeout is
    // throttled in the background but still fires.
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = setTimeout(() => {
        this.#frame = null;
        this.sync();
      }, 0);
    };

    // The picker morphs the section rather than emitting a variant event the
    // theme exposes, so the DOM itself is the signal. Mutations arrive in
    // bursts during a morph, which is what the debounce is for.
    new MutationObserver(schedule).observe(this.#root, { childList: true, subtree: true });

    // The stepper sets input.value as a property, which fires no event at all
    // and mutates no attribute — so neither the listeners below nor the
    // observer above would see it. The component's own event is the only
    // signal, and it bubbles. Synchronous, because these are cheap and a user
    // pressing "+" should not wait on a timer.
    document.addEventListener('quantity-selector:update', () => this.sync());
    this.#root.addEventListener('input', () => this.sync());
    this.#root.addEventListener('change', () => this.sync());
  }

  sync() {
    const variant = this.#variant;
    if (!variant) return;

    this.#syncButton(variant);
    this.#syncBadge(variant);
    this.#syncVariantPrices();
    this.#syncIncluded(variant);
  }

  /**
   * Show the "what's included" panel belonging to the selected variant.
   *
   * The block renders one panel per variant and hides all but the current one.
   * It has to be done here rather than server-side because the variant picker
   * morphs only part of the section — the block sits outside that part, so a
   * single server-rendered panel stayed on whichever variant the page loaded
   * with, showing the wrong contents for every other option.
   */
  #syncIncluded(variant) {
    const root = this.#root.querySelector('.bo-inc');
    if (!root) return;

    const panels = root.querySelectorAll('.bo-inc__set');
    if (!panels.length) return;

    let matched = false;

    panels.forEach((panel) => {
      const isCurrent = String(panel.dataset.boVariant) === String(variant.id);
      if (isCurrent) matched = true;
      if (panel.hidden === !isCurrent) return;
      panel.hidden = !isCurrent;
    });

    // A variant with neither its own list nor a product fallback has no panel
    // at all; the block steps aside rather than leaving an empty frame.
    const shouldHide = !matched;
    if (root.hidden !== shouldHide) root.hidden = shouldHide;
  }

  /**
   * Print each option's own price under its label.
   *
   * Only for single-option products: with two or more options an option value
   * does not identify a variant ("Red" spans every size), so any number shown
   * would be a guess. Better to show nothing than something plausible.
   */
  #syncVariantPrices() {
    if (!this.#data.showVariantPrice) return;

    const labels = this.#root.querySelectorAll('.variant-option__button-label');
    if (!labels.length) return;

    if (this.#data.optionCount !== 1) return;

    labels.forEach((label) => {
      const value = label.querySelector('input')?.value;
      const variant = this.#data.variants.find((v) => v.title === value);
      if (!variant) return;

      let price = label.querySelector('.bo-vp__price');
      if (!price) {
        price = document.createElement('span');
        price.className = 'bo-vp__price';
        price.setAttribute('aria-hidden', 'true');
        label.appendChild(price);
      }

      setText(price, formatMoney(variant.price, this.#data.moneyFormat));
    });
  }

  #syncButton(variant) {
    // Both buttons are fed by the same arithmetic. Keeping one code path is
    // what stops the bar and the buy box ever disagreeing about the total —
    // they are on screen together, so a mismatch would be obvious.
    if (this.#data.showButtonPrice) {
      this.#priceOnButton(this.#root.querySelector('.product-details .add-to-cart-button'), variant);
    }

    if (this.#data.showStickyPrice) {
      this.#priceOnButton(this.#root.querySelector('.sticky-add-to-cart__button'), variant);
    }
  }

  /** Append (or refresh) the price shown inside one add-to-cart button. */
  #priceOnButton(button, variant) {
    if (!button) return;

    // Sold out or unavailable: the button says so, and a price beside that
    // reads as an offer the shopper cannot take.
    if (!variant.available || button.disabled) {
      button.querySelector('.bo-atc__meta')?.remove();
      return;
    }

    let meta = button.querySelector('.bo-atc__meta');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'bo-atc__meta';
      meta.setAttribute('aria-hidden', 'true');
      meta.innerHTML = '<span class="bo-atc__rule"></span><span class="bo-atc__price"></span>';
      // Before the checkmark burst, which is an overlay and must stay last.
      const burst = button.querySelector('.add-to-cart__added');
      burst ? button.insertBefore(meta, burst) : button.appendChild(meta);
    }

    setText(meta.querySelector('.bo-atc__price'), formatMoney(variant.price * this.#quantity, this.#data.moneyFormat));
  }

  #syncBadge(variant) {
    if (!this.#data.showBadge) return;

    const price = this.#root.querySelector('.product-details product-price');
    if (!price) return;

    const onSale = variant.compareAt > variant.price;
    let badge = price.querySelector('.bo-pdp__badge');

    if (!onSale) {
      badge?.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'bo-pdp__badge';
      price.appendChild(badge);
    }

    const saved = Math.round(((variant.compareAt - variant.price) / variant.compareAt) * 100);
    setText(badge, `−${saved}%`);
  }
}

const start = (root = document) => {
  root.querySelectorAll?.(SECTION).forEach((section) => {
    if (section.dataset.boPdpReady) return;
    section.dataset.boPdpReady = 'true';
    new ProductPageSync(section);
  });
};

start();

// The theme editor tears a section down and rebuilds it on every setting change.
document.addEventListener('shopify:section:load', (event) => start(event.target));
